import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { readEmailLinkToken, hasHashSession } from "@/lib/emailLinkParams";
import { reportError } from "@/lib/reportError";
import {
  RESEND_COOLDOWN_SECONDS,
  resendConfirmationEmail,
} from "@/lib/resendConfirmation";
import KinAvatar from "@/components/KinAvatar";

// Landing route for the Supabase email-confirmation link (emailRedirectTo in
// SignUp.tsx points here).
//
// ── TWO LINK SHAPES, both live (#626) ──────────────────────────────────────
//
// 1. ?token_hash=…&type=signup   — the NEW shape. The tap target is
//    app.kinyoudoitall.com, which is an associated domain, so iOS can hand the
//    tap to the Median app. We call verifyOtp here and the session is
//    established in whatever context opened the link — the app.
//
// 2. #access_token=…&type=signup — the OLD shape. Supabase verified on its own
//    domain and 302-redirected here; supabase-js (detectSessionInUrl, on by
//    default) parses the fragment and fires SIGNED_IN.
//
// ⚠️ Shape 2 must keep working. Confirmation emails already in inboxes carry
// it, and the template change is a Dashboard setting flipped by hand — there
// is no moment when every outstanding link switches over. Dropping it would
// break confirmation for people who have not yet gotten into the app at all.
//
// ⚠️ Why #228 did not fix this: iOS fires a Universal Link only on a tap whose
// URL is ALREADY on an associated domain, never on a server-side redirect into
// one. #228 fixed the landing path and left the tap target on supabase.co, so
// Safari always won. The AASA file was never the problem — it was verified
// correct and serving 200 while the bug was live.
//
// This path is a DEDICATED route (not bare "/") specifically so it can be listed
// in the AASA paths array without routing every link to the domain into the app.
// Do NOT redirect the signup confirmation back to bare "/" — that path can't be
// a Universal Link target.
//
// Per CLAUDE.md: read auth state from useAuth(), never call supabase.auth
// .getSession()/getUser() directly in a component. verifyOtp is a different
// thing — it is the action that CREATES the session, not a read of one.
//
// ── #1532 — the expired link used to be a dead end ──────────────────────────
//
// This screen used to set `verifyFailed` and, on that SAME state change, both
// render "That link's expired" and fire a navigate to /signin. useEffect runs
// after paint, so the sentence survived about one frame and she landed on Sign
// In with no idea why. From there nothing offered a new link: the only resend
// in the app lived behind SignUp's `confirmationSent` state, which is set only
// by submitting that form in the same session.
//
// So the expired state STAYS now, and it carries the fix — one tap for a fresh
// link. ⚠️ Do not reintroduce a navigate that fires on the render that shows
// the message; that was the defect.
//
// The success path still navigates to "/" with replace, deliberately: the spent
// confirmation URL must not sit in history. (#1529 documents an @sentry/browser
// bug that mislabels the transaction for the rest of the session when that
// navigate is the page's first history mutation. That is an SDK guard bug, not
// ours, and is being fixed separately — do not "fix" it by changing this
// navigate.)
const AuthCallback = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Read the URL once. Both of these are pure reads of a value that cannot
  // change while this component is mounted.
  const token = useMemo(() => readEmailLinkToken(window.location.search), []);
  const hasHash = useMemo(() => hasHashSession(window.location.hash), []);

  // A landing carrying NEITHER shape is already dead — there is nothing to wait
  // for. #1532 wires up hasHashSession for exactly this, per its docstring: it
  // used to have no production caller at all, so every empty landing sat
  // through the full six-second grace window before admitting anything.
  const [expired, setExpired] = useState(!token && !hasHash);

  // verifyOtp consumes a single-use token. React 18 StrictMode double-invokes
  // effects in development, and a second call on a spent token fails — which
  // would show "that link's expired" on a link that just worked.
  const verifyStarted = useRef(false);

  // ── The new shape: exchange the token for a session, right here ──────────
  useEffect(() => {
    if (verifyStarted.current) return;
    if (!token) return; // legacy hash link, or nothing — handled elsewhere
    verifyStarted.current = true;

    void (async () => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token.tokenHash,
        type: token.type,
      });
      if (error) {
        // Expired, already used, or tampered with.
        //
        // reportError puts the tags on the scope BEFORE the console line, so
        // the event captureConsoleIntegration synthesises carries them too —
        // the plain console.error this used to be produced an untagged event
        // and dropped `type` entirely, so signup / recovery / magiclink /
        // invite were indistinguishable in Sentry. See lib/reportError.ts.
        reportError("[AuthCallback] verifyOtp failed", error, {
          tags: { auth_flow: token.type, error_class: "expired_link" },
        });
        setExpired(true);
        return;
      }
      // On success supabase-js fires SIGNED_IN, AuthProvider surfaces it, and
      // the effect below navigates. Nothing to do here — deliberately NOT
      // navigating from inside this callback, so there is exactly one place
      // that decides where she lands.
    })();
  }, [token]);

  // Once the session resolves, head home.
  useEffect(() => {
    if (!loading && user) {
      navigate("/", { replace: true });
    }
  }, [loading, user, navigate]);

  // Grace window for the HASH token to parse before we treat it as a dead link.
  // Only armed for the legacy shape: on the token_hash path the verifyOtp call
  // above is the signal, and a slow network must not race a timer into showing
  // "expired" on a link that is still being exchanged.
  useEffect(() => {
    if (token || !hasHash) return;
    const t = setTimeout(() => setExpired(true), 6000);
    return () => clearTimeout(t);
  }, [token, hasHash]);

  const showExpired = expired && !user;

  if (showExpired) {
    return <ExpiredLink linkType={token?.type} />;
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center" style={{ backgroundColor: "#F0F0FF" }}>
      <div className="text-center px-6">
        <div className="flex justify-center mb-3">
          <KinAvatar size="md" variant="dark" />
        </div>
        <p className="text-sm font-body" style={{ color: "#54525C" }}>
          Confirming your email…
        </p>
      </div>
    </div>
  );
};

/**
 * The terminal state for a link that cannot be used. It STAYS on screen — she
 * reads it and acts on it here, rather than being bounced somewhere that does
 * not explain itself.
 */
const ExpiredLink = ({ linkType }: { linkType?: string }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // A password-reset link is not re-sendable from here: resend() only speaks
  // signup/email_change, and asking GoTrue to re-confirm an already-confirmed
  // account fails. "Forgot password?" on the sign-in screen is the real path,
  // so say that instead of offering a button that cannot work.
  const isRecovery = linkType === "recovery";

  const handleResend = async () => {
    if (!email.trim()) {
      setNotice("Pop your email in above first 💜");
      return;
    }
    setSending(true);
    setNotice(null);
    // resendConfirmationEmail READS the returned error and hands back what to
    // say. Never confirm over an unread write (guardrail 16).
    const outcome = await resendConfirmationEmail(email);
    setSending(false);
    setNotice(outcome.message);
    if (outcome.cooldown) setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <div className="h-[100dvh] overflow-y-auto" style={{ backgroundColor: "#F0F0FF" }}>
      <div
        className="min-h-full flex flex-col items-center justify-center px-6 py-10"
        style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="w-full max-w-sm flex flex-col items-center text-center">
          <KinAvatar size="lg" variant="dark" />
          <h1 className="font-display mt-5 mb-3" style={{ fontSize: 28, color: "#1C1A3E" }}>
            That link's expired.
          </h1>

          {isRecovery ? (
            <>
              <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5 }}>
                Password reset links only last a little while. Head to sign in and tap
                "Forgot password?" for a fresh one.
              </p>
              <Link
                to="/signin"
                className="w-full h-12 mt-6 font-body font-semibold text-base text-white flex items-center justify-center"
                style={{ backgroundColor: "#5050C8", borderRadius: "14px" }}
              >
                Go to sign in
              </Link>
            </>
          ) : (
            <>
              <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5 }}>
                Confirmation links don't last forever. Pop your email in and we'll send a
                fresh one.
              </p>

              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full h-12 px-4 mt-6 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
                style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
              />

              <button
                onClick={handleResend}
                disabled={sending || cooldown > 0}
                data-testid="callback-resend"
                className="w-full h-12 mt-3 font-body font-semibold text-base text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#5050C8", borderRadius: "14px" }}
              >
                {sending
                  ? "Sending…"
                  : cooldown > 0
                    ? `Send me a fresh link (${cooldown}s)`
                    : "Send me a fresh link"}
              </button>

              {notice && (
                <p className="mt-3 font-body text-sm" style={{ color: "#5050C8" }}>
                  {notice}
                </p>
              )}

              <Link to="/signin" className="mt-4 font-body" style={{ fontSize: 14, color: "#54525C" }}>
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
